/**
 * Hook for transaction form handlers
 * Extracted from Activity.tsx to improve code organization
 */

import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Account, TransactionCustomFieldValues } from "../../../types";
import { formatRate, getCurrentTime } from "../../../utils/transactionUtils";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

export interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string; // Rate from transaction currency to default currency
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: Record<string, any>;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

export interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

export interface UseTransactionFormHandlersParams {
  // State
  formData: FormData;
  transferFormData: TransferFormData;
  defaultCurrency: string;
  accounts: Account[];
  isMobile: boolean;
  workspaceSlug: string | undefined;
  // State setters
  setFormData: (data: FormData | ((prev: FormData) => FormData)) => void;
  setTransferFormData: (data: TransferFormData | ((prev: TransferFormData) => TransferFormData)) => void;
  setEditingTransaction: (transaction: any | null) => void;
  setInitialFormData: (data: FormData | null) => void;
  setInitialTransferFormData: (data: TransferFormData | null) => void;
  setDialogOpen: (open: boolean) => void;
  setTransferDialogOpen: (open: boolean) => void;
  setDialogType: (type: "expense" | "income") => void;
  setShowTime: (show: boolean) => void;
  setShowTransferTime: (show: boolean) => void;
  setShowTransferFee?: (show: boolean) => void;
  setRateFromTransaction: (fromTransaction: boolean) => void;
  setRateManuallyEdited: (edited: boolean) => void;
  setDefaultCurrencyRateManuallyEdited: (edited: boolean) => void;
  setTransferRateManuallyEdited: (edited: boolean) => void;
  // Helper functions
  getAccountById: (accountId: string | undefined) => Account | undefined;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
}

export interface UseTransactionFormHandlersResult {
  handleAddClick: (type: "expense" | "income" | "transfer") => void;
  handleAccountChange: (accountId: string) => void;
  handleRefreshRate: () => void;
  handleRefreshDefaultCurrencyRate: () => void;
  handleRefreshTime: () => void;
  handleRefreshTransferRate: () => void;
  handleRefreshTransferTime: () => void;
  getConvertedAmount: () => number | null;
}

/**
 * Hook for transaction form handlers
 */
export const useTransactionFormHandlers = (
  params: UseTransactionFormHandlersParams
): UseTransactionFormHandlersResult => {
  const {
    formData,
    transferFormData,
    defaultCurrency,
    accounts,
    isMobile,
    workspaceSlug,
    setFormData,
    setTransferFormData,
    setEditingTransaction,
    setInitialFormData,
    setInitialTransferFormData,
    setDialogOpen,
    setTransferDialogOpen,
    setDialogType,
    setShowTime,
    setShowTransferTime,
    setRateFromTransaction,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    setTransferRateManuallyEdited,
    getAccountById,
    calculateTransferRate,
  } = params;

  const navigate = useNavigate();

  // Calculate converted amount for live display
  const getConvertedAmount = useCallback((): number | null => {
    if (!transferFormData.fromAccountId || !transferFormData.toAccountId || !transferFormData.amount) {
      return null;
    }

    const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId);
    const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId);

    if (!fromAccount || !toAccount) return null;

    const currenciesDiffer = fromAccount.currency !== toAccount.currency;
    if (!currenciesDiffer) return null;

    const amount = parseFloat(transferFormData.amount);
    if (isNaN(amount) || amount <= 0) return null;

    if (!transferFormData.transferRate || transferFormData.transferRate.trim() === "") {
      return null;
    }

    const rate = parseFloat(transferFormData.transferRate);
    if (isNaN(rate) || rate <= 0) return null;

    return amount * rate;
  }, [transferFormData, accounts]);

  // Handle refresh rate from settings
  const handleRefreshRate = useCallback((): void => {
    if (!formData.accountId || !formData.currency) return;
    
    const account = accounts.find((acc) => acc.id === formData.accountId);
    if (!account) return;
    
    const accountCurrency = account.currency || defaultCurrency;
    if (formData.currency !== accountCurrency) {
      const calculatedRate = calculateTransferRate(formData.currency, accountCurrency);
      setFormData((prev) => ({
        ...prev,
        rate: formatRate(calculatedRate),
      }));
      // Reset flags - rate is now from settings, not from transaction or manual edit
      setRateFromTransaction(false);
      setRateManuallyEdited(false);
    }
  }, [formData.accountId, formData.currency, accounts, defaultCurrency, calculateTransferRate, setFormData, setRateFromTransaction, setRateManuallyEdited]);
  
  // Handle refresh default currency rate from settings
  const handleRefreshDefaultCurrencyRate = useCallback((): void => {
    if (!formData.accountId || !formData.currency) return;
    
    const account = accounts.find((acc) => acc.id === formData.accountId);
    if (!account) return;
    
    const accountCurrency = account.currency || defaultCurrency;
    
    if (formData.currency !== defaultCurrency && accountCurrency !== defaultCurrency) {
      // Calculate rate from account currency to default currency (not expense currency)
      const calculatedRate = calculateTransferRate(accountCurrency, defaultCurrency);
      setFormData((prev) => ({
        ...prev,
        defaultCurrencyRate: formatRate(calculatedRate),
      }));
      // Reset flag - rate is now from settings
      setDefaultCurrencyRateManuallyEdited(false);
    }
  }, [formData.accountId, formData.currency, accounts, defaultCurrency, calculateTransferRate, setFormData, setDefaultCurrencyRateManuallyEdited]);
  
  // Handle refresh time to current time
  const handleRefreshTime = useCallback((): void => {
    setFormData((prev) => ({
      ...prev,
      time: getCurrentTime(),
    }));
  }, [setFormData]);

  // Handle refresh transfer time to current time
  const handleRefreshTransferTime = useCallback((): void => {
    setTransferFormData((prev) => ({
      ...prev,
      time: getCurrentTime(),
    }));
  }, [setTransferFormData]);

  // Handle refresh transfer rate from settings
  const handleRefreshTransferRate = useCallback((): void => {
    if (!transferFormData.fromAccountId || !transferFormData.toAccountId) return;
    
    const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId);
    const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId);
    
    if (!fromAccount || !toAccount) return;
    
    if (fromAccount.currency !== toAccount.currency) {
      const calculatedRate = calculateTransferRate(fromAccount.currency, toAccount.currency);
      setTransferFormData((prev) => ({
        ...prev,
        transferRate: formatRate(calculatedRate),
      }));
      // Reset flag - rate is now from settings
      setTransferRateManuallyEdited(false);
    }
  }, [transferFormData.fromAccountId, transferFormData.toAccountId, accounts, calculateTransferRate, setTransferFormData, setTransferRateManuallyEdited]);

  // Handle add transaction
  const handleAddClick = useCallback((type: "expense" | "income" | "transfer"): void => {
    console.log("🔄 [Activity] handleAddClick called:", { type, isMobile, workspaceSlug, bypassAuth: isBypassAuthEnabled() });
    // On mobile, navigate to full-page transaction creation
    if (isMobile && workspaceSlug) {
      const targetPath = `/${workspaceSlug}/create-transaction/${type}`;
      console.log("🔄 [Activity] Navigating to mobile transaction page:", targetPath);
      navigate(targetPath);
      return;
    }

    // On desktop, use dialog
    if (type === "transfer") {
      setEditingTransaction(null);
      setInitialTransferFormData(null);
      setTransferFormData({
        fromAccountId: "",
        toAccountId: "",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        time: "",
        transferRate: "",
      });
      setShowTransferTime(false);
      setTransferDialogOpen(true);
    } else {
      setDialogType(type);
      setEditingTransaction(null);
      setInitialFormData(null);
      setFormData({
        title: "",
        category: "",
        amount: "",
        currency: defaultCurrency,
        rate: "",
        defaultCurrencyRate: "",
        date: new Date().toISOString().split("T")[0],
        time: "",
        accountId: "",
        accountGroup: "",
      });
      setShowTime(false);
      setRateFromTransaction(false);
      setRateManuallyEdited(false);
      setDialogOpen(true);
    }
  }, [
    isMobile,
    workspaceSlug,
    navigate,
    setEditingTransaction,
    setInitialTransferFormData,
    setTransferFormData,
    setShowTransferTime,
    setTransferDialogOpen,
    setDialogType,
    setInitialFormData,
    setFormData,
    defaultCurrency,
    setShowTime,
    setRateFromTransaction,
    setRateManuallyEdited,
    setDialogOpen,
  ]);

  // Handle account selection change
  const handleAccountChange = useCallback((accountId: string): void => {
    const account = getAccountById(accountId);
    if (account) {
      // Don't change currency when account is selected - keep the selected currency
      const accountCurrency = account.currency || defaultCurrency;
      const currentCurrency = formData.currency || defaultCurrency;

      // Recalculate rate for the new account: transaction currency → account currency
      let newRate = "";
      if (currentCurrency !== accountCurrency) {
        const calculatedRate = calculateTransferRate(currentCurrency, accountCurrency);
        newRate = formatRate(calculatedRate);
      }

      // Recalculate default currency rate for the new account: account currency → default currency
      let newDefaultCurrencyRate = "";
      if (accountCurrency !== defaultCurrency) {
        const calculatedAccountToDefaultRate = calculateTransferRate(accountCurrency, defaultCurrency);
        newDefaultCurrencyRate = formatRate(calculatedAccountToDefaultRate);
      }

      setFormData((prev) => ({
        ...prev,
        accountId: accountId,
        accountGroup: account.group || "",
        currency: currentCurrency,
        rate: newRate,
        defaultCurrencyRate: newDefaultCurrencyRate,
      }));
      setRateManuallyEdited(false);
      setDefaultCurrencyRateManuallyEdited(false);
    } else {
      setFormData((prev) => ({
        ...prev,
        accountId: accountId,
        accountGroup: "",
        currency: prev.currency || defaultCurrency,
        rate: "",
        defaultCurrencyRate: "",
      }));
    }
  }, [getAccountById, defaultCurrency, formData.currency, setFormData, calculateTransferRate, setRateManuallyEdited, setDefaultCurrencyRateManuallyEdited]);

  return {
    handleAddClick,
    handleAccountChange,
    handleRefreshRate,
    handleRefreshDefaultCurrencyRate,
    handleRefreshTime,
    handleRefreshTransferRate,
    handleRefreshTransferTime,
    getConvertedAmount,
  };
};
